import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ArticleCard from './components/ArticleCard';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import Footer from "../../components/ui/Footer";
import blogs from "../../data/blogs";

const AuthorsDeskArticlesCommentary = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [activeTags, setActiveTags] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  const categories = Array.from(new Set(blogs.map(article => article.category)));

  const articleCounts = categories?.reduce((acc, category) => {
    acc[category] = blogs?.filter(article => article?.category === category)?.length;
    return acc;
  }, {});

  useEffect(() => {
    let filtered = blogs;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered?.filter(blog => blog?.category === activeCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter(blog =>
          blog?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          blog?.excerpt?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          blog?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          blog?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    // Filter by active tags
    if (activeTags?.length > 0) {
      filtered = filtered?.filter(blog =>
        activeTags?.some(tag => blog?.tags?.includes(tag))
      );
    }

    // Sort articles
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date) - new Date(a.date);
        case 'title':
          return a?.title?.localeCompare(b?.title);
        default:
          return 0;
      }
    });

    setFilteredArticles(filtered);
  }, [activeCategory, searchTerm, activeTags, sortBy]);

  const handleTagClick = (tagName) => {
    setActiveTags(prev => 
      prev?.includes(tagName) 
        ? prev?.filter(tag => tag !== tagName)
        : [...prev, tagName]
    );
  };

  const clearFilters = () => {
    setActiveCategory('all');
    setSearchTerm('');
    setActiveTags([]);
  };

  return (
    <>
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-20 lg:pt-24 pb-12 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="PenTool" size={24} className="text-primary-foreground" />
                </div>
                <h1 className="font-headline text-4xl lg:text-5xl font-bold text-primary">
                  Blog
                </h1>
              </div>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <SearchBar onSearch={setSearchTerm} />
            </div>

            {/* Active Filters */}
            {(activeCategory !== 'all' || activeTags?.length > 0 || searchTerm) && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm font-medium text-scholarly-gray">Filtros activos:</span>
                
                {activeCategory !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">
                    {activeCategory}
                    <button
                      onClick={() => setActiveCategory('all')}
                      className="ml-2 hover:bg-primary-foreground/20 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                
                {activeTags?.map(tag => (
                  <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-accent text-accent-foreground">
                    {tag}
                    <button
                      onClick={() => handleTagClick(tag)}
                      className="ml-2 hover:bg-accent-foreground/20 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))}
                
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground">
                    "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-2 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-scholarly-gray hover:text-primary"
                >
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-8">
                <CategoryFilter
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  articleCounts={articleCounts}
                />
              </div>

              {/* Main Content Area */}
              <div className="lg:col-span-3">
                {/* Sort Options */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <h2 className="font-headline text-2xl font-semibold text-primary">
                      {activeCategory === 'all' ? 'Todos los artículos' : activeCategory}
                    </h2>
                    <span className="text-sm text-scholarly-gray">
                      ({filteredArticles?.length} {filteredArticles?.length === 1 ? 'artículo' : 'artículos'})
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-scholarly-gray">Ordenar por:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="text-sm bg-card border literary-border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="date">Fecha</option>
                      <option value="title">Título</option>
                    </select>
                  </div>
                </div>

                {/* Articles Grid */}
                {filteredArticles?.length > 0 ? (
                  <div className="space-y-8">
                    {/* Featured Article */}
                    <ArticleCard article={filteredArticles?.[0]} variant="featured" />
                    
                    {/* Regular Articles Grid */}
                    {filteredArticles?.length > 1 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredArticles?.slice(1)?.map((article) => (
                          <ArticleCard key={article?.id} article={article} />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Search" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                      No se encontraron artículos
                    </h3>
                    <p className="text-scholarly-gray mb-4">
                      Intenta ajustar los filtros o términos de búsqueda.
                    </p>
                    <Button variant="outline" onClick={clearFilters}>
                      Limpiar filtros
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <Footer/>
      </div>
    </>
  );
};

export default AuthorsDeskArticlesCommentary;